<template>
    <TooltipComponent
        ref="genericTooltip"
        :show-delay="0"
        :offset="offset"
    >
        <div v-if="personData" class="person-tooltip-content">
            <!-- Name -->
            <h4>{{ personData.name }}</h4>

            <!-- Core Identifiers -->
            <p v-if="personData.internal_number">
                {{ trans('Internal number') }}: {{ personData.internal_number }}
            </p>
            <p v-if="personData.badge_number">
                {{ trans('Badge number') }}: {{ personData.badge_number }}
            </p>

            <hr v-if="personData.internal_number || personData.badge_number">

            <!-- Job & Status -->
            <p v-if="personData.job?.name">
                {{ trans('Job') }}: {{ personData.job.name }}
            </p>
            <p v-if="personData.status?.name" class="d-flex align-items-center">
                {{ trans('Status') }}:
                <span
                    class="status-dot ml-1 mr-1"
                    :style="{ backgroundColor: personData.status.color }"
                ></span>
                {{ personData.status.name }}
            </p>

            <!-- Department & Team -->
             <p v-if="personData.department?.name">
                {{ trans('Department') }}: {{ personData.department.name }}
            </p>
             <p v-if="personData.team?.name">
                {{ trans('Team') }}: {{ personData.team.name }}
            </p>

            <!-- Latest Badge Time -->
            <div v-if="personData.latestBadgeTime?.id" class="badge-time-section">
                <hr>
                <p class="d-flex align-items-center">
                    {{ trans('Badge') }}:
                    <span
                        class="status-dot ml-1 mr-1"
                        :class="personData.latestBadgeTime.type" 
                        :style="{ backgroundColor: getBadgeDotColor(personData.latestBadgeTime.type) }"
                    ></span>
                    <span class="text-muted small ml-1">
                        {{ formatDateTime(personData.latestBadgeTime.date, personData.latestBadgeTime.time) }}
                    </span>
                </p>
            </div>

            <!-- Skills -->
            <div v-if="personData.skills?.length" class="skills-section">
                 <hr>
                 <p><strong>{{ trans('Skills') }}:</strong></p>
                 <ul>
                     <li
                        v-for="skill in personData.skills"
                        :key="'skill-'+skill.id"
                        class="d-flex align-items-center justify-content-between"
                    >
                         <span>{{ skill.name }}</span>
                         <star-rating
                            :value="skill.stars"
                            :id="skill.id"
                            :readonly="true"
                         />
                     </li>
                 </ul>
            </div>

            <!-- Certificates -->
             <div v-if="personData.certificates?.length" class="certificates-section">
                 <hr>
                 <p><strong>{{ trans('Certificates') }}:</strong></p>
                 <ul>
                     <li v-for="cert in personData.certificates" :key="'cert-'+cert.id">
                         {{ cert.name }}
                         <span v-if="cert.date" class="text-muted small">
                             ({{ trans('Valid') }}: {{ formatDate(cert.date) }})
                         </span>
                         <span v-else class="text-muted small">({{ trans('No date') }})</span>
                     </li>
                 </ul>
             </div>

            <!-- Date Specific Info -->
            <div v-if="personData.weekschedule_on_date || personData.slots_on_date?.length || personData.absences_on_date?.length">
                <hr>

                <!-- Weekschedule -->
                <div v-if="personData.weekschedule_on_date && personData.weekschedule_on_date.hasOwnProperty('date')" class="weekschedule-section">
                    <p>{{ personData.weekschedule_on_date.title || trans('Work Schedule') }}</p>
                    <p class="detail-times">
                        <span v-if="personData.weekschedule_on_date.is_working">
                            {{ personData.weekschedule_on_date.time_start }} - {{ personData.weekschedule_on_date.time_end }} | <strong>{{ formatDuration(personData.weekschedule_on_date.duration) }}</strong>
                        </span>
                        <span v-else class="detail-times">
                            0:00
                        </span>
                    </p>
                </div>

                <!-- Absences -->
                  <div v-if="personData.absences_on_date?.length" class="absences-section">
                        <hr>
                      <ul>
                          <li v-for="absence in personData.absences_on_date" :key="'absence-on-date-'+absence.id">
                              {{ absence.type_name }}
                              <span class="detail-times">
                                  {{ absence.time_start }} - {{ absence.time_end }} | {{ formatDuration(absence.duration) }}
                              </span>
                          </li>
                          <li class="total-item">
                              <strong class="detail-times">
                                  {{ formatDuration(calculateTotalDuration(personData.absences_on_date)) }}
                              </strong>
                          </li>
                      </ul>
                  </div>

                                  <!-- Slots/Planning -->
                  <div v-if="personData.slots_on_date?.length" class="slots-section">
                    <hr>
                    <ul>
                        <li v-for="slot in personData.slots_on_date" :key="'slot-on-date-'+slot.id">
                            {{ slot.task_name }} <span v-if="slot.project_name">| {{ slot.project_name }}</span>
                            <span class="detail-times">
                                {{ slot.start_time }} - {{ slot.end_time }} | {{ formatDuration(slot.duration) }}
                            </span>
                        </li>
                        <li class="total-item">
                            <strong class="detail-times">
                                {{ formatDuration(calculateTotalDuration(personData.slots_on_date)) }}
                            </strong>
                        </li>
                    </ul>
                  </div>
            </div>

        </div>
        <!-- Fallback/Loading/Error slots could go here if needed in the future -->
    </TooltipComponent>
</template>

<script>
import axios from 'axios';
import TooltipComponent from "../helpers/TooltipComponent.vue";
import { LocalDate, LocalTime, LocalDateTime, DateTimeFormatter, ZoneId } from '@js-joda/core';
import { transform } from 'lodash';

export default {
    name: 'PersonTooltipComponent',
    components: { TooltipComponent }, // StarRatingComponent is global
    props: {
        fetchDelay: { type: Number, default: 700 }, // Delay before fetch starts
        offset: {
            type: Array,
            default: () => [0, 0]
        },
    },
    data() {
        return {
            personData: null,
            isLoading: false,
            fetchError: false,
            pendingFetchTimeoutId: null,
            eventForPendingAction: null,
            personIdForPendingAction: null,
            triggerElementForPendingAction: null,
            fetchIsInProgress: false,
            currentFetchingPersonId: null,
            currentFetchController: null,
            dateForPendingAction: null,
        };
    },
    methods: {
        // --- PUBLIC API ---
        activateTooltipForPerson(event, personId, date) {
            if (!event || !event.currentTarget || !personId) return;
            const triggerElement = event.currentTarget;

            const genericTooltip = this.$refs.genericTooltip;
            if (genericTooltip) {
                if (genericTooltip.isVisible && genericTooltip.triggerElementCurrentlyVisibleFor !== triggerElement) {
                    genericTooltip.publicHide();
                }
                else if (genericTooltip.isVisible && genericTooltip.triggerElementCurrentlyVisibleFor === triggerElement) {
                    return;
                }
                genericTooltip.publicStopExternalTracking(); // Ensure previous tracking is stopped
                genericTooltip.publicStartExternalTracking(event);
            }

            this._cancelPendingActionAndFetch();

            this.eventForPendingAction = event;
            this.personIdForPendingAction = personId;
            this.triggerElementForPendingAction = triggerElement;
            this.dateForPendingAction = date;

            this.pendingFetchTimeoutId = setTimeout(() => {
                if (this.triggerElementForPendingAction === triggerElement && this.personIdForPendingAction === personId) {
                    const savedPersonId = this.personIdForPendingAction;
                    const savedTriggerElement = this.triggerElementForPendingAction;
                    const savedDate = this.dateForPendingAction;
                    this.pendingFetchTimeoutId = null;
                    this._fetchDataAndShowTooltip(savedPersonId, savedTriggerElement, savedDate);
                }
            }, this.fetchDelay);
        },

        deactivateOrCancelTooltip(event, personId) {
            if (!event || !event.currentTarget) return;
            const leavingTrigger = event.currentTarget;
            const genericTooltip = this.$refs.genericTooltip;

            if (this.triggerElementForPendingAction === leavingTrigger && this.personIdForPendingAction === personId) {
                this._cancelPendingActionAndFetch();
            }

            if (genericTooltip && genericTooltip.isVisible && genericTooltip.triggerElementCurrentlyVisibleFor === leavingTrigger) {
                 genericTooltip.publicHide();
                 genericTooltip.publicStopExternalTracking();
            }
            if (this.triggerElementForPendingAction === leavingTrigger) {
                if (genericTooltip) {
                    genericTooltip.publicStopExternalTracking();
                }
            }
        },

        // --- Internal Logic ---
        _cancelPendingActionAndFetch() {
            if (this.pendingFetchTimeoutId) clearTimeout(this.pendingFetchTimeoutId);
            this.pendingFetchTimeoutId = null;
            if (this.fetchIsInProgress && this.currentFetchController) this.currentFetchController.abort();

            const genericTooltip = this.$refs.genericTooltip;
            if (genericTooltip) {
                genericTooltip.publicStopExternalTracking();
            }

            this.eventForPendingAction = null;
            this.personIdForPendingAction = null;
            this.triggerElementForPendingAction = null;
            this.dateForPendingAction = null;
        },

        async _fetchDataAndShowTooltip(personId, triggerElement, date) {
            if (this.fetchIsInProgress) {
                 if(this.currentFetchController) this.currentFetchController.abort();
            }
            this.isLoading = true;
            this.fetchError = false;
            this.personData = null;
            this.fetchIsInProgress = true;
            this.currentFetchingPersonId = personId;
            this.currentFetchController = new AbortController();
            const signal = this.currentFetchController.signal;

            try {
                // --- Real API Call ---
                const url = route('persons.info', { personId: personId }); // Basic URL structure - adjust if needed

                const params = {};
                if (date) {
                    params.date = date;
                }

                const response = await axios.get(url, {
                    signal: signal,
                    params: params
                });

                this.personData = response.data.person;
                this.isLoading = false;

                // NOW show the tooltip, using the LATEST mouse position FROM TooltipComponent.
                const genericTooltip = this.$refs.genericTooltip;
                if (genericTooltip) {
                    const positionData = genericTooltip.publicConsumeLatestExternalMousePosition();

                    if (positionData) {
                        if (!genericTooltip.isVisible) {
                            genericTooltip.publicShowNow(positionData, triggerElement);
                        } else if (genericTooltip.triggerElementCurrentlyVisibleFor !== triggerElement) {
                            genericTooltip.publicHide();
                            genericTooltip.publicShowNow(positionData, triggerElement);
                        } else {
                            genericTooltip.publicShowNow(positionData, triggerElement);
                        }
                    } else {
                        console.warn("PersonTooltipComponent: Cannot show tooltip, latest mouse position data is missing from TooltipComponent.");
                    }
                } else {
                     console.warn("PersonTooltipComponent: Cannot show tooltip, genericTooltip ref is missing.");
                }

            } catch (err) {
                this.personData = null;
                this.isLoading = false;

                if (!(axios.isCancel(err) || (err.name === 'AbortError'))) {
                     console.error('PersonTooltipComponent: Fetch Error:', err);
                     this.fetchError = true;
                } else {
                    console.log('PersonTooltipComponent: Fetch aborted.');
                }

                 const genericTooltip = this.$refs.genericTooltip;
                 if (genericTooltip && genericTooltip.isVisible && genericTooltip.triggerElementCurrentlyVisibleFor === triggerElement) {
                      genericTooltip.publicHide();
                 }
            } finally {
                const genericTooltip = this.$refs.genericTooltip;
                if (genericTooltip) {
                    genericTooltip.publicStopExternalTracking();
                }

                if (this.currentFetchingPersonId === personId && this.currentFetchController?.signal === signal) {
                    this.fetchIsInProgress = false;
                    this.currentFetchController = null;
                    this.currentFetchingPersonId = null;
                }
            }
        },

        // --- Formatting Helpers ---
        formatDate(dateString) {
            if (!dateString) return '';
            try {
                // dateString is now guaranteed to be YYYY-MM-DD
                const localDate = LocalDate.parse(dateString);
                const formatter = DateTimeFormatter.ofPattern('dd/MM/yyyy');
                return localDate.format(formatter);
            } catch (e) {
                console.error("Error formatting date with js-joda:", dateString, e);
                return dateString; // Return original if formatting fails
            }
        },

        formatDuration(durationInMinutes) {
            if (durationInMinutes === null || typeof durationInMinutes === 'undefined') return '';
            const hours = Math.floor(durationInMinutes / 60);
            const minutes = durationInMinutes % 60;
            return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
        },

        formatDateTime(dateString, timeString) {
             if (!dateString || !timeString) return '';
             try {
                 // dateString is now guaranteed to be YYYY-MM-DD
                 const localDate = LocalDate.parse(dateString);

                 // Parse HH:mm from timeString
                 const localTime = LocalTime.parse(timeString);

                 const localDateTime = LocalDateTime.of(localDate, localTime);
                 const formatter = DateTimeFormatter.ofPattern('dd/MM/yyyy HH:mm');
                 return localDateTime.format(formatter);
             } catch (e) {
                 console.error("Error formatting datetime with js-joda:", dateString, timeString, e);
                 return `${dateString} ${timeString}`; // Fallback to YYYY-MM-DD HH:mm
             }
        },

        getBadgeDotColor(type) {
            switch (type?.toLowerCase()) {
                case 'in': return '#28a745'; // Green
                case 'out': return '#dc3545'; // Red
                default: return '#6c757d'; // Gray
            }
        },

        calculateTotalDuration(items) {
            if (!items || !items.length) return 0;
            return items.reduce((total, item) => {
                return total + (item.duration || 0);
            }, 0);
        }
    },
    beforeUnmount() {
        this._cancelPendingActionAndFetch();
    }
};
</script>

<style scoped>
.person-tooltip-content {
    padding: 10px 15px;
    min-width: 300px;
    text-align: left;
    line-height: 1.4;
}
.person-tooltip-content h4 {
    margin-top: 0;
    margin-bottom: 10px;
    font-size: 1.2em;
    color: #333;
    border-bottom: 1px solid #eee;
    padding-bottom: 5px;
}
.person-tooltip-content p,
.person-tooltip-content li {
    margin-bottom: 5px; /* Consistent spacing */
    font-size: 0.9em; /* Base for p and li, relative to tooltip root */
    color: #6E7C7E;
}

/* NEW: Ensure p inside li inherits li's font-size directly to avoid double scaling */
.person-tooltip-content li p {
    font-size: 1em; /* Make p font-size relative to li's font-size (which is 0.9em of root) */
}

.person-tooltip-content p > strong,
.person-tooltip-content li > strong {
    color: #333; /* Darker color for labels */
}
.person-tooltip-content hr {
    border: none;
    border-top: 1px solid #eee;
    margin: 10px 0;
}

.status-dot {
    display: inline-block;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    vertical-align: middle; /* Align with text */
}

.badge-time-section .text-muted,
.certificates-section .text-muted {
    color: #889999 !important; /* Lighter gray */
}

.skills-section ul,
.certificates-section ul,
.absences-section ul,
.slots-section ul {
    list-style: none;
    padding-left: 0;
    margin-top: 5px;
}

/* Style for list items in all sections */
.skills-section li,
.certificates-section li,
.absences-section li,
.slots-section li {
    margin-bottom: 5px;
    position: relative;
}

/* Adjust star rating alignment if needed - depends on the component's internal structure */
.skills-section li span:first-child {
    margin-right: 10px; /* Space between skill name and stars */
}

/* Ensure global styles for .br-theme-icofont-stars are loaded */
/* You might need to adjust margin/padding if the star-rating component has defaults */

/* Add margin utilities if not globally available */
.ml-1 { margin-left: 0.25rem !important; }
.mr-1 { margin-right: 0.25rem !important; }

/* Add flex utilities if not globally available */
.d-flex { display: flex !important; }
.align-items-center { align-items: center !important; }
.justify-content-between { justify-content: space-between !important; }

/* Remove unused daily-info-section classes */
.daily-info-section {
    margin-bottom: 8px;
}

.detail-times { /* This is the <span> containing the actual time/duration/status text */
    display: block; /* Ensures full width for text-align to work on spans */
    text-align: right;
    color: #718096;   /* Specific color for detail text */
}

/* List item styling for items with detail-times in weekschedule, absences, and slots sections */
.absences-section li .detail-times,
.slots-section li .detail-times {
    display: block;
    margin-top: 2px;
    text-align: right;
}

/* Total item styling */
.total-item{
    text-align: right;
}
.total-item strong{
    border-top: 1px solid #6E7C7E;
    color: #6E7C7E !important;
}

.total-item .detail-times {
    display: inline !important;
}
</style>